import { uiText } from '../../i18n/uiText';
import React, { useEffect, useState } from 'react';
import {
  FileCheck, CheckCircle2, AlertCircle, AlertTriangle, ExternalLink,
  ChevronDown, ChevronUp, Paperclip,
} from 'lucide-react';
import { selfCheckApi, getApiErrorMessage } from '../../api';

/**
 * R11 п.2: контекст self-check проверяемого в карточке аудита.
 *
 * FTT 4.4: «Аудитор видит результаты self-check компании при подготовке к аудиту».
 *
 * Использование на AuditDetailPage:
 *   <SelfCheckSummary companyId={audit?.meta?.audited_company?.id} />
 */
const COLOR_META = {
  green:  { label: uiText("Зелёный (надёжный)"), bg: '#dcfce7', color: '#15803d', icon: CheckCircle2 },
  yellow: { label: uiText("Жёлтый (внимание)"),  bg: '#fef3c7', color: '#a16207', icon: AlertCircle },
  red:    { label: uiText("Красный (критический)"), bg: '#fee2e2', color: '#b91c1c', icon: AlertTriangle },
};

export default function SelfCheckSummary({ companyId }) {
  const [check, setCheck] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!companyId) { setLoading(false); return; }
    let alive = true;
    Promise.all([
      selfCheckApi.getCompany(companyId),
      selfCheckApi.definition().catch(() => ({ data: { questions: [] } })),
    ])
      .then(([checkRes, definitionRes]) => {
        if (!alive) return;
        setCheck(checkRes.data);
        setQuestions(definitionRes.data?.questions || []);
      })
      .catch(e => {
        if (!alive) return;
        if (e.response?.status === 404) setError(uiText("Компания не заполнила Self-check"));
        else setError(getApiErrorMessage(e));
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [companyId]);

  if (loading) return <div style={{ padding: 12, color: '#94a3b8', fontSize: 12 }}>{uiText("Загрузка self-check…")}</div>;

  if (!companyId) return null;

  if (error) {
    return (
      <div className="card" style={{ marginBottom: 14 }}>
        <div className="card-body" style={{ padding: 14, display: 'flex', alignItems: 'center', gap: 10, color: 'var(--premium-text-secondary)' }}>
          <FileCheck size={16} style={{ opacity: 0.5 }}/>
          <div>
            <div style={{ fontWeight: 600, fontSize: 13 }}>{uiText("Self-check проверяемого")}</div>
            <div style={{ fontSize: 12, marginTop: 2 }}>{error}</div>
          </div>
        </div>
      </div>
    );
  }

  if (!check) return null;

  const cm = check.rating_color ? COLOR_META[check.rating_color] : null;
  const Icon = cm?.icon || FileCheck;

  // Считаем ответы Да/Нет
  const answers = check.answers || {};
  const yes = Object.values(answers).filter(a => (typeof a === 'object' ? a.value : a) === true).length;
  const no = Object.values(answers).filter(a => (typeof a === 'object' ? a.value : a) === false).length;
  const docs = (check.documents || []).length;

  return (
    <div className="card" style={{ marginBottom: 14, overflow: 'hidden' }}>
      <div className="self-check-summary-header" style={{
        padding: '12px 16px',
        background: cm ? `linear-gradient(90deg, ${cm.bg} 0%, white 80%)` : '#f8fafc',
        borderBottom: '1px solid #f1f5f9',
        display: 'flex', alignItems: 'center', gap: 12,
      }}>
        <div className="self-check-summary-icon" style={{
          width: 36, height: 36, borderRadius: 9, background: 'white',
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
        }}>
          <Icon size={18} style={{ color: cm?.color || '#64748b' }}/>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>{uiText("Self-check проверяемого")}</div>
          <div style={{ fontWeight: 700, fontSize: 14, marginTop: 2, color: cm?.color || '#475569' }}>
            {cm?.label || uiText("Не рассчитан")}
          </div>
        </div>
        {check.rating_score != null && (
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 11, color: '#64748b' }}>{uiText("Балл")}</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: cm?.color || '#475569', lineHeight: 1 }}>
              {check.rating_score}<span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 500 }}>/100</span>
            </div>
          </div>
        )}
        <button type="button" onClick={() => setExpanded(!expanded)}
          style={{
            padding: 8, border: 'none', background: 'transparent', cursor: 'pointer', color: '#64748b',
          }}>
          {expanded ? <ChevronUp size={18}/> : <ChevronDown size={18}/>}
        </button>
      </div>

      {!expanded && (
        <div style={{ padding: '8px 16px', display: 'flex', gap: 14, fontSize: 12, color: '#475569' }}>
          <span>{uiText("✓ Ответов «Да»: ")}<b>{yes}</b></span>
          <span>{uiText("✗ Ответов «Нет»: ")}<b style={{ color: no > 0 ? '#b91c1c' : '#475569' }}>{no}</b></span>
          <span className="workspace-inline-meta"><Paperclip size={13} />{uiText(" Документов: ")}<b>{docs}</b></span>
          <span style={{ marginLeft: 'auto', color: '#94a3b8' }}>
            {check.status === 'reviewed' ? uiText("✓ Проверен") : check.status === 'submitted' ? uiText("Ожидает проверки") : check.status}
          </span>
        </div>
      )}

      {expanded && (
        <div style={{ padding: 14 }}>
          <div style={{ fontSize: 11, color: '#64748b', marginBottom: 8 }}>{uiText("Этот опросник заполнен проверяемым перед аудитом. Сравнивайте заявленное с фактическим.")}</div>
          <table style={{ width: '100%', fontSize: 12 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 600, color: '#64748b' }}>{uiText("Вопрос")}</th>
                <th style={{ textAlign: 'center', padding: '6px 8px', fontWeight: 600, color: '#64748b', width: 70 }}>{uiText("Ответ")}</th>
                <th style={{ textAlign: 'center', padding: '6px 8px', fontWeight: 600, color: '#64748b', width: 80 }}>{uiText("Документ")}</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(answers).sort(([left], [right]) => {
                const leftOrder = questions.find((item) => item.id === left)?.order || Number(left.replace(/\D/g, '')) || 0;
                const rightOrder = questions.find((item) => item.id === right)?.order || Number(right.replace(/\D/g, '')) || 0;
                return leftOrder - rightOrder;
              }).map(([qid, a]) => {
                const val = typeof a === 'object' ? a.value : a;
                const question = questions.find((item) => item.id === qid);
                const questionOrder = question?.order || qid.replace('q', '');
                const docForQ = (check.documents || []).find(d => String(d.checklist_item) === String(questionOrder));
                return (
                  <tr key={qid} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '8px' }}>
                      <div style={{ fontWeight: 600 }}>{question ? `${question.order}. ${question.title}` : qid}</div>
                      {question?.description ? <div className="workspace-muted" style={{ marginTop: 2 }}>{question.description}</div> : null}
                    </td>
                    <td style={{
                      textAlign: 'center', padding: '6px 8px',
                      color: val === true ? '#15803d' : val === false ? '#b91c1c' : '#94a3b8',
                      fontWeight: 600,
                    }}>{val === true ? uiText("✓ Да") : val === false ? uiText("✗ Нет") : '—'}</td>
                    <td style={{ textAlign: 'center', padding: '6px 8px' }}>
                      {docForQ ? <Paperclip size={14} style={{ color: '#15803d' }} /> : <span style={{ color: '#cbd5e1' }}>—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
